import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { theme } from 'styles/theme/hav-theme'

type ProductDetailContainerProps = {
  mixedBoxView: boolean
  showAlert: boolean
}

const mixedBox = {
  view: (showAlert: ProductDetailContainerProps['showAlert']) => css`
    grid-template-columns: 500px 1fr;
    grid-template-areas:
      'lastUpdate productInfo'
      'mixedBox mixedBox';
    gap: ${theme.spacing.xs};

    ${
      showAlert &&
      css`
      grid-template-rows: auto;
      grid-template-areas:
        'lastUpdate productInfo'
        'mixedBox mixedBox'
        'alert alert';
    `
    }
    @media (max-width: ${theme.breakpoints.lg}px) {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      grid-template-areas:
        'lastUpdate'
        'mixedBox'
        'productInfo'
        'alert';
    }
  `,
}

export const ProductDetailContainer = styled.div<ProductDetailContainerProps>`
  ${({ mixedBoxView, showAlert }) => css`
    display: grid;
    row-gap: ${theme.spacing.sm};
    grid-template-columns: 1fr 1.5fr;
    grid-template-areas:
      'lastUpdate productInfo'
      'alert alert';

    @media (max-width: ${theme.breakpoints.lg}px) {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      grid-template-areas:
        'lastUpdate'
        'productInfo'
        'alert';
    }
    ${mixedBoxView && mixedBox.view(showAlert)}
    margin-bottom: ${theme.spacing.sm};
  `}
`

export const LastUpdateWrapper = styled.div`
  grid-area: lastUpdate;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.high};
  font-weight: ${theme.typography.weight.xl};
`

export const LastUpdateContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  border-radius: 6px;
  padding: 0 ${theme.spacing.sm};
  background-color: ${theme.colors.alert.info.ligther};

  p {
    color: ${theme.colors.alert.info[40]} !important;
  }
`

export const AlertContainer = styled.div`
  grid-area: alert;
  padding: ${theme.spacing.xs} 0;
`

export const MixedBoxHelper = styled.div`
  max-width: fit-content;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  font-size: ${theme.typography.sizes['2xs']};
  line-height: ${theme.typography.lineHeight.md};

  strong {
    font-weight: ${theme.typography.weight.md};
  }
  p {
    font-weight: ${theme.typography.weight.xsm};
    margin: 0;
    max-width: 1250px;
    white-space: pre-wrap;
  }
`

export const DetailsContainer = styled.div`
  grid-area: productInfo;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.lg}px) {
    gap: ${theme.spacing.xm};
  }
`

export const CheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
`

export const MixedBoxContainer = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.weight.lg};
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.lg}px) {
    font-size: ${theme.typography.sizes.xs};
  }
`

export const MixedBoxQtdContainer = styled.div`
  width: 110px;
  margin-left: ${theme.spacing.md};
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  width: fit-content;
  font-size: ${theme.typography.sizes.xs};
`

export const TypingBy = styled(Flex)`
  gap: 0;

  span {
    padding-top: 2px;
    font-family: ${theme.typography.family.primary};
    font-weight: ${theme.typography.weight.lg};
  }
`

export const WrapBoxFilter = styled('div')`
  display: flex;
  position: relative;
`

export const ContentPopoverHeader = styled('div')`
  padding: ${theme.spacing.md};
  display: flex;
  justify-content: space-between;
`

export const ContentPopoverButton = styled('div')`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
`

export const ContentWrapperPopover = styled('div')`
  padding: 0 ${theme.spacing.md};
`

export const ContentPopover = styled('div')`
  width: 400px;
  max-height: 165px;
  display: flex;
  flex-wrap: wrap;
  overflow-y: scroll;
  margin-bottom: ${theme.spacing.md};
  ::-webkit-scrollbar {
    width: ${theme.shapes.borderRadius.md};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.neutral.light};
    border-radius: ${theme.shapes.borderRadius.md};
    border: none;
  }
  ::-webkit-scrollbar-track {
    background: ${theme.colors.neutral.clear};
    border-radius: ${theme.shapes.borderRadius.md};
  }

  > div {
    flex-basis: 50%;
    margin-bottom: ${theme.spacing.sm};
  }
  div {
    width: 100%;
  }

  #CheckboxItem {
    font-size: ${theme.typography.sizes.xs};
    color: ${theme.colors.neutral.medium};
    @media screen and (max-width: 425px) {
      font-size: ${theme.typography.sizes.xs};
    }
  }
  @media screen and (max-width: 425px) {
    padding-left: calc(3 * ${theme.spacing.xs});
  }

  @media screen and (max-width: 425px) {
    width: 100%;
  }
`

export const NoResultFindBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  flex-basis: 100% !important;
  #NoItemFoundTitle {
    text-align: center;
    margin-top: ${theme.spacing['2xm']};
    font-size: ${theme.typography.sizes.lg};
    font-weight: ${theme.typography.weight.lg};
    margin-bottom: ${theme.spacing.sm};
    color: ${theme.colors.neutral.black};
  }

  #NoItemFoundDescription {
    text-align: center;
    font-size: ${theme.typography.sizes.sm};
    line-height: ${theme.typography.lineHeight.md};
    color: ${theme.colors.neutral.black};
  }
`

export const DividerStyled = styled('hr')`
  margin: ${theme.spacing.md};
  border-top: ${theme.shapes.borderWidth.thin} ${theme.colors.neutral.lighthover};
  border-right: none;
  border-bottom: none;
  border-left: none;
  margin-top: 0;
`
